<template>
  <div class="service-request-page-step1">
    <div class="service-request-page__top">
      <div class="service-request-page__head">
        <div class="service-request-page__input">
          <SearchField
            ref="SearchField"
            v-model="inputSearch"
            required
            :label="
              choiceSearch === 0
                ? getMain.form.city_id.label
                : getMain.form.dealer_id.label
            "
            @clickClearIcon="clearSearch"
            @clickSearchIcon="handleSearch"
          />
        </div>
        <div class="service-request-page__checkboxes">
          <div
            class="service-request-page__checkbox-inner"
            @click="onSearchCity"
          >
            <button
              class="service-request-page__checkbox"
              :class="{ active: choiceSearch === 0 }"
            >
              <div class="radio" />
              <span> {{ getMain.buttonSearchByCity }} </span>
            </button>
          </div>

          <div
            class="service-request-page__checkbox-inner"
            @click="onSearchDP"
          >
            <button
              class="service-request-page__checkbox"
              :class="{ active: choiceSearch === 1 }"
            >
              <div class="radio" />
              <span> {{ getMain.buttonSearchByDealer }} </span>
            </button>
          </div>

          <button
            class="service-request-page__geo"
            @click="getCurrentGeoLocation"
          >
            <span>{{ getMain.tabs.tab2.labels.useMyLocation }}</span>
            <span class="icon icon-locate" />
          </button>
        </div>
      </div>
      <BaseButton
        class="service-request-page__button"
        type="primary"
        :disabled="!form.dealerId"
        @click="submitForm"
      >
        {{ getMain.continueButtonText }}
        <IconArrow class="icon-left" />
      </BaseButton>
    </div>
    <div v-show="showMap " class="service-request-page__inner">
      <div
        class="service-request-page-step1__search-results-list"
        :class="{ 'without-map': choiceSearch === 1 }"
      >
        <div
          v-show="error"
          class="service-request-page-step1__location-block-error"
        >
          {{ getMain.notFoundLabel }}
        </div>
        <div
          v-for="(item, itemIndex) in items"
          :key="`item-${itemIndex}`"
          ref="$items"
          class="service-request-page-step1__search-results-list-item"
          :class="{
            'active': activeItemIndex === itemIndex
          }"
          @click="onMarkerClick(itemIndex,'list')"
        >
          <div
            class="service-request-page-step1__search-results-list-item-title"
          >
            {{ item._source.name }}
            <span
              class="service-request-page-step1__search-results-list-item-index"
            >
              {{ itemIndex + 1 }}
            </span>
          </div>
          <div
            v-if="getWorkshopAddress(item)"
            class="service-request-page-step1__search-results-info-description"
          >
            {{ getWorkshopAddress(item).addressLine1 }} {{ getWorkshopAddress(item).addressLine2 }}
          </div>

          <div
            v-if="item.workshopOpenDays"
            class="service-request-page-step1__schedule"
          >
            <div class="service-request-page-step1__schedule-title">
              {{ scheduleTitle }}
            </div>
            <div
              v-for="(line, lineIndex) in item.workshopOpenDays.split('\n')"
              :key="`schedule-${itemIndex}-${lineIndex}`"
              class="service-request-page-step1__schedule-line"
            >
              {{ line }}
            </div>
          </div>

          <div
            v-if="choiceSearch === 0 "
            class="service-request-page-step1__search-results-list-item-distance"
          >
            <GeoIcon />
            {{
              getDistanceBetweenTwoPoints(
                {
                  lat: item._source.addresses[0].geoLocation.lat,
                  lon: item._source.addresses[0].geoLocation.lon
                },
                {
                  lat: currentGeoLocation.latitude,
                  lon: currentGeoLocation.longitude
                }
              )
            }}
            {{ getMain.tabs.tab2.labels.kilometer }}
          </div>

          <BaseButton
            class="service-request-page-step1__search-results-list-item-button"
            type="secondary"
          >
            {{ getMain.tabs.tab2.labels.select }}
            <IconArrow class="icon-left" />
          </BaseButton>
          <BaseButton
            class="service-request-page-step1__search-results-list-item-button-active"
            type="secondary"
          >
            <SuccessIcon />
            {{ getMain.selectedDealerText }}
          </BaseButton>
        </div>
      </div>
      <div
        v-show="choiceSearch === 0"
        class="service-request-page-step1__map-wrapper"
      >
        <div class="service-request-page-step1__map-toggle-wrapper">
          <button
            v-if="!isDesktop"
            class="service-request-page-step1__map-toggle"
            @click="isMapExpandedMobile = !isMapExpandedMobile"
          >
            {{ isMapExpandedMobile ? hideMapText : showMapText }}
          </button>
        </div>
        <div
          class="service-request-page-step1__map-inner"
          :class="{ 'map-inner-hidden': !isDesktop && !isMapExpandedMobile }"
        >
          <div
            class="service-request-page-step1__map map"
            :class="{ 'map-visible': showMap }"
          />
        </div>
      </div>
    </div>
    <div
      v-if="isShowMoreButton"
      class="service-request-page__more"
      @click="showMoreItems"
    >
      {{ getMain.loadMoreItemsText }}
      <MoreIcon />
    </div>
  </div>
</template>

<script>
  import axios from 'axios'
  import { mapGetters, mapState } from 'vuex'
  import GeoIcon from '@/assets/img/icons/geo.svg?inline'
  import SuccessIcon from '@/assets/img/icons/success.svg?inline'
  import MoreIcon from '@/assets/img/icons/more.svg?inline'
  import IconArrow from '@/assets/img/icons/arrow-long-right.svg?inline'
  import SearchField from '@/components/main/SearchField.vue'
  import { getDistanceBetweenTwoPoints } from '@/helpers/helpers'
  import debounce from 'lodash/debounce'
  import { shuffle } from 'lodash'

  export default {
    name: 'Step1',

    components: {
      SearchField,
      IconArrow,
      GeoIcon,
      SuccessIcon,
      MoreIcon
    },
    props: {
      form: [Object, String],
      dealerItems: Array,
      title: String,
      hint: String,
      activeLoading: Boolean,
      doneStepIndex: Number,
      showroom: String,
      errorMsgs: Object,
      submitForm: Function,
      changeFieldMap: Function,
      resetStep: Function
    },

    data () {
      return {
        showMap: false,
        isMapExpandedMobile: false,
        activeItemIndex: '',
        inputSearch: '',
        error: false,
        language: this.$store.state.currentLang,
        country: this.$store.state.currentCountry,
        currentGeoLocation: {
          latitude: 0,
          longitude: 0
        },
        items: [],
        markers: [],
        page: 1,
        perPage: 4,
        totalCount: 0,
        pageCount: 0,
        choiceSearch: 0,
        debouncedGetDealersCity: null,
        currentCancelToken: null,
        showMoreButton: false
      }
    },
    computed: {
    ...mapGetters(['getMain', 'isShow', 'getPhoneType']),
    ...mapState({
      currentLang: state => state.currentLang,
      currentCountry: state => state.currentCountry
    }),
    isDesktop () {
      return this.$store.state.deviceSize.width >= 1200
    },
    showMapText () {
      return (this.getMain.tabs && this.getMain.tabs.tab2 && this.getMain.tabs.tab2.labels && this.getMain.tabs.tab2.labels.showMap) || 'Показати мапу'
    },
    hideMapText () {
      return (this.getMain.tabs && this.getMain.tabs.tab2 && this.getMain.tabs.tab2.labels && this.getMain.tabs.tab2.labels.hideMap) || 'Сховати мапу'
    },
    scheduleTitle () {
      return (this.getMain.tabs && this.getMain.tabs.tab2 && this.getMain.tabs.tab2.labels && this.getMain.tabs.tab2.labels.worktime) || 'Графік роботи:'
    },
    isShowMoreButton () {
      return this.items.length < this.totalCount
    }
    },

    watch: {
      choiceSearch () {
        if (this.currentCancelToken) {
          this.currentCancelToken.cancel()
        }
        this.error = false
        this.inputSearch = ''
        this.resetPagination()

        this.items = []
        this.markers = []
        if (this.map) {
          this.map.clearMarkers()
        }
      },
      inputSearch (newVal) {
        this.error = false
        if (this.currentCancelToken) {
          this.currentCancelToken.cancel()
        }
        if (this.choiceSearch === 1 && newVal.length >= 3) {
          this.debouncedGetDealersCity(newVal)
        } else {
          this.debouncedGetDealersCity.cancel()
        }
      }
    },

    mounted () {
      this.initMap()
      this.initAutoComplete()
    },
    created () {
      this.debouncedGetDealersCity = debounce((val) => {
        this.getAllDealersCity(true, val)
      }, 350)
    },

    methods: {
      getDistanceBetweenTwoPoints,
      resetPagination () {
        this.page = 1
        this.pageCount = 0
        this.totalCount = 0
      },
      handleSearch () {
        if (this.inputSearch.length) {
          this.resetPagination()
          if (this.map) {
            this.map.clearMarkers()
          }
          this.getAllDealersCity(false, this.inputSearch)
        }
      },
      onSearchCity () {
        if (this.map) {
          this.map.clearMarkers()
          this.$nextTick()
        }
        this.autocomplete.enable()
        this.items = []

        this.choiceSearch = 0
        this.error = false
        document.querySelector(
          '.toyota-maps-autocomplete-wrapper'
        ).style.opacity = 1
        document.querySelector(
          '.toyota-maps-autocomplete-wrapper'
        ).style.zIndex = 1
      },
      onSearchDP () {
        if (this.map) {
          this.map.clearMarkers()
          this.$nextTick()
        }
        this.autocomplete.disable()

        this.items = []

        this.choiceSearch = 1
        this.error = false
        document.querySelector(
          '.toyota-maps-autocomplete-wrapper'
        ).style.opacity = 0
        document.querySelector(
          '.toyota-maps-autocomplete-wrapper'
        ).style.zIndex = 0
      },
      animateMarker (index) {
        this.map.markers.forEach(marker => {
          marker.element.classList.remove('marker-animate')
        })
        if (this.map.markers[index].element) {
          this.map.markers[index].element.classList.add('marker-animate')
        }

        if (!this.map.markers && !this.map.markers[index]) return
        this.map.markers[index].animate()
      },
      showMoreItems () {
        this.activeLoading = true
        if (this.page < this.pageCount) {
          this.page++
          if (this.choiceSearch === 0) {
            this.getAllDealers()
          }
          if (this.choiceSearch === 1) {
            this.getAllDealersCity(this.choiceSearch === 1, this.inputSearch)
          }
        }
      },
      async onMarkerClick (index, type) {
        await this.changeFieldMap(this.items[index].id, this.items[index]._source, false)
        this.$nextTick()
        const $el = this.items[index]._source
        if ($el) {
          if (type === 'list') {
            if (window.parent) {
              window.parent.postMessage({ scrollToIframe: true }, '*')
            }
            window.scrollTo({
              top: 0,
              behavior: 'smooth'
            })
          }

          if (this.choiceSearch === 0) {
            if (!$el.addresses[0]) {
              return console.error('No address')
            }
            this.map._mboxMap.flyTo({
              duration: 1000,
              center: [$el.addresses[0].geoLocation.lon, $el.addresses[0].geoLocation.lat],
              zoom: 14
            })
            this.animateMarker(index)
          }
          this.activeItemIndex = index
        }
      },

      async initMap () {
        const $map = this.$el.querySelector('.map')
        if (!$map) return
        this.map = await new window.mapsWrapper.DynamicMap($map, {
          accessToken: this.getMain.tabs.tab2.labels.mapboxKey,
          language: this.language,
          countryCode: this.country,
          addCountryBoundaries: this.country,
          zoomControl: this.$resp.sm ? 'top-left' : 'bottom-right',
          maxZoom: 15
        })
      },

      async getCurrentGeoLocation () {
        if (this.map) {
          this.map.clearMarkers()
          this.$nextTick()
        }
        this.$emit('set-dealer', '')
        this.activeItemIndex = ''
        this.showMap = true
        this.choiceSearch = 0
        this.error = false
        const currentlocation = await this.map.getCurrentGeoLocation()

        if (currentlocation.length) {
          this.currentGeoLocation.latitude = currentlocation[1]
          this.currentGeoLocation.longitude = currentlocation[0]
        }

        const [location] = await this.map.geoReverse(currentlocation, {
          fitToBounds: true,
          limit: 1,
          language: this.language
        })

        if (location instanceof Object && location.name) {
          this.inputSearch = location.name
        }

        const options = {
          enableHighAccuracy: true,
          timeout: 5000,
          maximumAge: 0
        }

        const success = async pos => {
          await this.getAllDealers()
          const crd = pos.coords
          this.currentGeoLocation.latitude = crd.latitude
          this.currentGeoLocation.longitude = crd.longitude
        }

        function error (err) {
          this.error = true
          console.warn(`ERROR(${err.code}): ${err.message}`)
        }

        navigator.geolocation.getCurrentPosition(success, error, options)
      },

      async getAllDealers () {
        this.$emit('set-dealer', '')
        this.activeItemIndex = ''

        try {
          const jsonData = {
            query: {
              bool: {
                must: {
                  term: {
                    'brands.raw': 'Lexus'
                  }
                },
                filter: {
                  geo_distance: {
                    distance: '200km',
                    'addresses.geoLocation': {
                      lat: this.currentGeoLocation.latitude,
                      lon: this.currentGeoLocation.longitude
                    }
                  }
                }
              }
            }
          }
          const CancelToken = axios.CancelToken

          this.currentCancelToken = CancelToken.source()

          const { data } = await window.axios.post(
            `/dealer/dealers-api?page=${this.page}&per_page=${this.perPage}`,
            jsonData,
            {
              headers: {
                Token: `${this.getMain['access-token']}`
              },
              cancelToken: this.currentCancelToken.token
            }
          )

          const hits = data.hits.hits

          if (this.page === 1) {
            this.items = hits
          } else {
            this.items = [...this.items, ...hits]
          }
          if (!data.hits.hits.length) {
            this.error = true
          }
          if (data.pagination) {
            this.totalCount = data.pagination.totalCount
            this.pageCount = data.pagination.pageCount
          }
          ;
          await this.getNearMarkers()
        } catch (e) {
        }
      },

      async getAllDealersCity (isDealerSearch, inputVal) {
        this.$emit('set-dealer', '')
        this.activeItemIndex = ''
        this.$emit('update:activeLoading', true)
        const filter = !isDealerSearch ? {
          geo_distance: {
            distance: '2000km',
            'addresses.geoLocation': {
              lat: this.currentGeoLocation.latitude || 50.272796,
              lon: this.currentGeoLocation.longitude || 30.31428
            }
          }
        } : {}
        const jsonData = {
          query: {
            bool: {
              must: {
                term: {
                  'brands.raw': 'Lexus'
                }
              },
              filter: {
              ...filter
              }
            }
          }
        }

        const CancelToken = axios.CancelToken

        this.currentCancelToken = CancelToken.source()

        try {
          const { data } = await window.axios.post(
            `/dealer/dealers-api?page=${this.page}&per_page=${this.perPage}&name=${inputVal}`,
            jsonData,
            {
              headers: {
                Token: `${this.getMain['access-token']}`
              },
              cancelToken: this.currentCancelToken.token
            }
          )
          const hits = shuffle(data.hits.hits)
          if (this.page === 1) {
            this.items = hits
          } else {
            this.items = [...this.items, ...hits]
          }
          this.activeItemIndex = null

          if (!data.hits.hits.length) {
            this.error = true
          }

          if (data.pagination) {
            this.totalCount = data.pagination.totalCount
            this.pageCount = data.pagination.pageCount
          }
          this.$emit('update:activeLoading', false)
          await this.getNearMarkers(false)
          this.$emit('update:activeLoading', false)
        } catch (e) {
          this.$emit('update:activeLoading', false)

          if (e.name === 'CanceledError') {
            return
          }
          console.log(e)
        }
      },

      async getNearMarkers (sortByGeo = true) {
        if (!this.showMap) this.showMap = true
        if (!this.map) return

        this.map.clearMarkers()

        this.items = this.items
          .map(item => {
            const distance = this.getDistanceBetweenTwoPoints(
              {
                lat: item._source.addresses[0].geoLocation.lat,
                lon: item._source.addresses[0].geoLocation.lon
              },
              {
                lat: this.currentGeoLocation.latitude,
                lon: this.currentGeoLocation.longitude
              }
            )
            return { ...item, distance }
          })

        if (sortByGeo) {
          this.items.sort((a, b) => a.distance - b.distance)
        }

        this.markers = this.items.map(dealer => ({
          lat: dealer._source.addresses[0].geoLocation.lat,
          lon: dealer._source.addresses[0].geoLocation.lon,
          title: dealer._source.name
        }))

        await this.map.addMarkers(this.markers, {
          markerImage: 'toyota-red-text',
          text: true,
          markerClick: this.onMarkerClick
        })

        await this.map.fitBoundsToMarkers({ maxZoom: 13 })
      },
      async initAutoComplete () {
        this.autocomplete = new window.mapsWrapper.AutoComplete({
          autoCompleteInput: this.$refs.SearchField.$el.querySelector('input'),
          countryCode: this.country,
          minLength: 3,
          delay: 500,
          language: this.language,
          types: ['place', 'locality', 'neighborhood', 'region', 'postcode'],
          onLocationSelect: async location => {
            if (this.choiceSearch === 1) return

            if (location instanceof Object) {
              [
                this.currentGeoLocation.longitude,
                this.currentGeoLocation.latitude
              ] = location.coordinates
              this.resetPagination()
              if (this.map) {
                this.map.clearMarkers()
              }
              await this.getAllDealers()
              this.error = false
            } else {
              this.error = true
            }
          },
          accessToken: this.getMain.tabs.tab2.labels.mapboxKey
        })
      },
      clearSearch () {
        this.map.clearMarkers()
        this.$emit('set-dealer', '')
        this.error = false
        this.items = []
        this.autocomplete.clearSuggestions()
        this.inputSearch = ''
        this.activeItemIndex = ''
        this.markers = []
        this.showMap = false
        this.resetStep()
        this.resetPagination()
      },
      getWorkshopAddress (item) {
        if (
          !item ||
          !item._source ||
          !item._source.addresses ||
          !item._source.addresses.length
        ) {
          return null
        }

        const addresses = item._source.addresses

        const workshopAddress = addresses.find(function (adr) {
          if (!adr || !Array.isArray(adr.operations)) return false

          return adr.operations.some(function (op) {
            return String(op).toLowerCase() === 'workshop'
          })
        })

        if (workshopAddress) {
          return workshopAddress
        }

        return addresses[0]
      } }
  }
</script>

<style lang="scss">
.service-request-page {

  &__top {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    width: 100%;
    gap: 24px;
    padding-bottom: 40px;
    border-bottom: 1px solid $c-gray-light;
    margin-bottom: 40px;
    @media (max-width: 1200px) {
      padding-bottom: 24px;
      margin-bottom: 24px;
      gap: 0;
    }
  }

  &__head {
    display: flex;
    align-items: center;
    gap: 24px;
    width: 100%;
    @media (max-width: 1200px) {
      flex-direction: column;
      align-items: flex-start;
    }
    @media (max-width: 767px) {
      gap: 16px;
      margin-bottom: 16px;
    }
  }

  &__checkboxes {
    display: flex;
    align-items: center;
    gap: 32px;
    @media (max-width: 767px) {
      flex-direction: column;
      align-items: flex-start;
      gap: 16px;
    }
  }

  &__checkbox {
    display: flex;
    gap: 16px;
    background: none;
    border: none;
    cursor: pointer;

    .radio {
      width: 20px;
      height: 20px;
      border-radius: 50%;
      border: 1px solid $c-gray-light;
      font-family: 'NobelWGL-Book';
      letter-spacing: 0.14px;
      margin: 0;
    }

    span {
      font-family: 'NobelWGL-Book';
      font-weight: 400;
      font-size: 14px;
      line-height: 20px;
      letter-spacing: 0.14;
      color: $c-black-mind;
    }

    &.active {
      .radio {
        position: relative;
        border: 4px solid $c-black-primary;
        color: $c-black-primary;

        &:after {
          position: absolute;
          left: 0;
          right: 0;
          top: 0;
          bottom: 0;
          content: '';
          width: 6px;
          height: 6px;
          border-radius: 50%;
          background: $c-black-primary;
          margin: auto;
        }
      }
    }
  }

  &__geo {
    position: relative;
    top: 2px;
    cursor: pointer;
    display: flex;
    gap: 8px;
    align-items: center;
    font-family: 'NobelWGL-Book';
    letter-spacing: 0.14px;
    font-size: 14px;
    line-height: 20px;
    font-weight: 400;
    background: transparent;
    border: none;
    color: $c-black-primary;
    padding: 0;

    span {
      color: $c-black-primary;
    }

    span:first-child {
      text-decoration: underline;
    }
  }

  &__input {
    max-width: 364px;
    width: 100%;
    @media (max-width: 767px) {
      max-width: 100%;
    }
  }

  &__button {
    &.button_disabled {
      opacity: 1;
      background: $c-gray-titan;
    }
  }

  .service-request-page-step1__map-wrapper {
    @media (max-width: 1199px) {
      width: 100%;
    }
  }
  .service-request-page-step1__map-toggle-wrapper{
    display: flex;
    justify-content: center;
  }

  .service-request-page-step1__map-toggle {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 12px;
    background: none;
    border: none;
    cursor: pointer;
    font-family: 'NobelWGL-Book';
    font-weight: 400;
    font-size: 20px;
    line-height: 24px;
    letter-spacing: 0.2px;
    color: $c-black-mind;

    &::after {
      content: '';
      display: block;
      width: 100%;
      height: 1px;
      background: $c-black-primary;
    }
  }

  .service-request-page-step1__map-inner {
    @media (max-width: 1199px) {
      display: grid;
      grid-template-rows: 610px;
      transition: grid-template-rows 0.4s ease, opacity 0.4s ease, margin 0.4s ease;
      opacity: 1;
      margin-top: 24px;
    }
    @media (max-width: 625px) {
      grid-template-rows: 400px;
    }
  }

  .map-inner-hidden {
    @media (max-width: 1199px) {
      grid-template-rows: 0px;
      opacity: 0;
      margin-top: 0;
      overflow: hidden;
    }
  }

  .service-request-page-step1__map {
    height: 610px;
    width: 558px;
    box-shadow: 0 4px 12px 0 #0000001F;
    border-radius: 12px;
    overflow: hidden;
    @media (max-width: 1200px) {
      width: 100%;
      min-height: 0;
    }
    @media (max-width: 625px) {
      height: 400px;
    }
  }

  .service-request-page-step1__search-results-list {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    grid-template-rows: repeat(2, 1fr);
    cursor: pointer;
    width: 100%;
    gap: 24px;
    max-width: 558px;
    @media (max-width: 1200px) {
      max-width: 100%;
    }
    @media (max-width: 625px) {
      grid-template-columns: repeat(1, 1fr);
    }

    &.without-map {
      max-width: 100%;
      grid-template-columns: repeat(4, 1fr);
      grid-template-rows: unset;
      margin-bottom: 24px;
      @media (max-width: 1200px) {
        grid-template-columns: repeat(2, 1fr);
        margin-bottom: 16px;
        gap: 16px;
      }
      @media (max-width: 767px) {
        grid-template-columns: 1fr;

      }
    }
  }

  .map {
    position: relative;
    opacity: 0 !important;
    visibility: hidden !important;
    transition: .35s !important;
    //transition-delay: 1s!important;
  }

  .map-visible {
    opacity: 1 !important;
    //transition-delay: 1s!important;
    visibility: visible !important;
  }

  .service-request-page-step1__search-results-list-item {
    padding: 24px;
    display: flex;
    flex-direction: column;
    border: 1px solid $c-gray-light;
    min-height: 320px;

    &.active {
      padding-bottom: 0;

      .service-request-page-step1__search-results-list-item-index {
        background: $c-green;
        color: $c-white;
      }

      .service-request-page-step1__search-results-list-item-button {
        display: none;
      }

      .service-request-page-step1__search-results-list-item-button-active {
        display: flex;
      }
    }
  }

  .service-request-page-step1__search-results-list-item-title {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 8px;
    font-family: 'NobelWGL-Book';
    font-weight: 400;
    font-size: 14px;
    line-height: 20px;
    letter-spacing: 2px;
    text-transform: uppercase;
    margin-bottom: 24px;
    padding-bottom: 24px;
    border-bottom: 1px solid $c-gray-light;
  }

  .service-request-page-step1__search-results-list-item-index {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    flex-shrink: 0;
    flex-grow: 0;
    border: 1px solid $c-gray-light;
    border-radius: 50%;
    font-family: 'NobelWGL-Book';
    font-weight: 400;
    font-size: 14px;
    line-height: 20px;
    letter-spacing: 0.14px;
  }

  .service-request-page-step1__search-results-list-item-distance {
    font-family: 'NobelWGL-Book';
    font-weight: 400;
    font-size: 16px;
    line-height: 20px;
    letter-spacing: 1px;
    gap: 8px;
    display: flex;
    align-items: center;
    margin-top: 24px;
    margin-bottom: 24px;
  }

  .service-request-page-step1__search-results-info-description {
    font-family: 'NobelWGL-Book';
    font-weight: 400;
    font-size: 14px;
    line-height: 20px;
    letter-spacing: 0.14px;
  }

  .service-request-page-step1__schedule {
    margin-top: 16px;
  }

  .service-request-page-step1__schedule-title {
    font-family: 'NobelWGL-Bold';
    font-weight: 700;
    font-size: 12px;
    line-height: 16px;
    letter-spacing: 0.12px;
    color: $c-black-mind;
    margin-bottom: 4px;
  }

  .service-request-page-step1__schedule-line {
    font-family: 'NobelWGL-Book';
    font-weight: 400;
    font-size: 12px;
    line-height: 18px;
    letter-spacing: 0.12px;
    color: $c-black-mind;
  }

  &__inner {
    gap: 24px;
    display: flex;
    @media (max-width: 1200px) {
      flex-direction: column-reverse;
    }
  }

  .service-request-page-step1__search-results-list-item-button {
    margin-top: auto;
    display: flex;
    font-family: 'NobelWGL-Bold';
    font-weight: 700;
    font-size: 14px;
    line-height: 20px;
    text-transform: uppercase;
    background: transparent;
    border: 1px solid $c-gray-light;
    border-radius: 4px;
    color: $c-azure;
    padding-left: 8px;
    padding-right: 8px;
    justify-content: center;

    svg path {
      fill: $c-azure;
    }

    &:focus {
      background: $c-azure;
      color: $c-white;

      svg path {
        fill: $c-white;
      }
    }

    @include hover {
      &:hover {
        background: $c-azure;
        color: $c-white;

        svg path {
          fill: $c-white;
        }
      }

    }
  }

  .service-request-page-step1__search-results-list-item-button-active {
    margin-top: auto;
    display: none;
    align-items: center;
    justify-content: flex-start;
    gap: 8px;
    font-family: 'NobelWGL-Book';
    font-weight: 400;
    font-size: 14px;
    line-height: 20px;
    letter-spacing: 0.14px;
    background: $c-green;
    border: 1px solid $c-green;
    position: relative;
    width: calc(100% + 48px) !important;
    color: $c-white;
    left: -24px;
    border-radius: 0;
    padding-left: 12px;

    &:hover, &:focus {
      background: $c-green !important;
      color: $c-white !important;
    }
  }

  .toyota-marker .main {
    fill: $c-black;
    stroke: $c-black;
    stroke-width: 3px;
  }

  .marker-animate .main {
    fill: $c-white;
  }

  .marker-animate text {
    fill: $c-black;
  }

  .service-request-page__more {
    display: flex;
    flex-direction: column;
    gap: 8px;
    align-items: center;
    font-family: NobelWGL-Book;
    font-weight: 400;
    font-size: 16px;
    line-height: 20px;
    letter-spacing: 1px;
  }
}

.service-request-page-step1 .service-request-page__button {
  @media (max-width: 1200px) {
    margin-top: 16px;
  }
}

</style>
